import React, { useMemo, useEffect, useState } from "react";
import { EGIconButton } from "../Button/IconButton/IconButton";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import EGDatePicker from "../DatePicker/EGDatePicker";
import { EGButton } from "../Button/EGButton/EGButton";
import { EGInput } from "../Input/EGInput";
import routeEN from "../../../translations/EN/report.json";
import routeES from "../../../translations/ES/report.json";
import dayjs from "dayjs";
import { EGBasicSelect } from "../Select/EGBasicSelect";
import { EGCheckbox } from "../Checkbox/EGCheckbox";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export const Filters = ({ initialFilters, onApply, defaultFilters }) => {
  const { t, i18n } = useTranslation("report");
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(initialFilters);

  useEffect(() => {
    if (!open) setLocalFilters(initialFilters);
  }, [open, initialFilters]);

  // Path optons
  const language = i18n.language;
  const reportersc = language === "es" ? routeES : routeEN;

  const eventOptions = Object.keys(reportersc.Rutas).map((key) => ({
    value: key,
    label: reportersc.Rutas[key],
  }));

  const hasLocalChanges = useMemo(() => {
    const df = defaultFilters;
    const lf = localFilters;

    const dateChanged =
      lf.date_range.active !== df.date_range.active ||
      lf.date_range.start !== df.date_range.start ||
      lf.date_range.end !== df.date_range.end;

    return (
      dateChanged ||
      (lf.user_email.active && lf.user_email.value !== "") ||
      (lf.path.active && lf.path.value !== "") ||
      (lf.folio.active && lf.folio.value !== "")
    );
  }, [localFilters, defaultFilters]);

  const handleFilterChange =
    (key, field = "value") =>
    (eventOrValue) => {
      setLocalFilters((prev) => {
        const newFilters = { ...prev };
        if (field === "active") {
          newFilters[key].active = eventOrValue.target.checked;
        } else {
          // Si es date_range
          if (key === "date_range" && field !== "active") {
            const d = dayjs(eventOrValue);
            const formatted = d.isValid() ? d.format("YYYY-MM-DD") : "";
            newFilters.date_range[field] = formatted;
            const { start, end } = newFilters.date_range;
            if (start && end && dayjs(start).isAfter(dayjs(end))) {
              if (field === "start") newFilters.date_range.end = start;
              if (field === "end") newFilters.date_range.start = end;
            }
          } else {
            newFilters[key].value = eventOrValue?.target
              ? eventOrValue.target.value
              : eventOrValue;
          }
        }
        return newFilters;
      });
    };

  // Seleccionar/deseleccionar todos
  const handleSelectAll = (checked) => {
    setLocalFilters((prev) => {
      const updated = {};
      for (const key in prev) {
        // prev[key] puede ser {active, value} o {active, start, end}
        updated[key] = { ...prev[key], active: checked };
      }
      return updated;
    });
  };

  const allActive = Object.values(localFilters).every((f) => f.active);
  const someActive = Object.values(localFilters).some((f) => f.active);

  const handleSubmit = () => {
    // normaliza antes de enviar
    const lf = { ...localFilters };
    if (lf.date_range.active) {
      const s = dayjs(lf.date_range.start);
      const e = dayjs(lf.date_range.end);
      if (s.isValid() && e.isValid() && s.isAfter(e)) {
        lf.date_range.end = s.format("YYYY-MM-DD");
        lf.date_range.start = e.format("YYYY-MM-DD");
      }
    }
    onApply(lf);
    setOpen(false);
  };

  const handleClose = () => setOpen(false);

  const handleClearLocal = () => {
    setLocalFilters(JSON.parse(JSON.stringify(defaultFilters)));
  };

  return (
    <>
      <EGIconButton
        iconName="settings4"
        sx={{ transform: "rotate(90deg)" }}
        iconColor="secondary"
        title={t("Filter.filter")}
        onClick={() => setOpen(true)}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="alert-dialog-slide-advance-search"
        maxWidth={"md"}
        fullWidth
      >
        <Box
          sx={{ position: "absolute", top: 10, right: 10 }}
          onClick={handleClose}
        >
          <EGIconButton iconName="cancel_circle" iconColor="primary" />
          <Typography
            variant="body2"
            textAlign="center"
            color="primary"
            mt={-0.7}
            fontSize={8}
            fontWeight={700}
          >
            ESC
          </Typography>
        </Box>

        <DialogTitle className="border-bottom" mx={1}>
          <Typography component="span" variant="h5" fontWeight={600} py={0.5}>
            {t("Filter.filter")}
          </Typography>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ mt: 1.5 }}>
          {/* Select All */}
          <Box sx={{ width: "100%", px: 0.5 }}>
            <EGCheckbox
              sx={{ maxWidth: "auto" }}
              checked={allActive}
              indeterminate={!allActive && someActive}
              onChange={(e) => handleSelectAll(e.target.checked)}
              label={
                allActive ? t("Filter.deselect_all") : t("Filter.select_all")
              }
              labelPlacement="end"
              color="primary"
            />
          </Box>
          {/* Filters */}
          <Stack
            alignItems="stretch"
            justifyContent="space-between"
            flexDirection="row"
            flexWrap="wrap"
            mt={1}
            px={2}
            sx={{ gap: 1 }}
          >
            {/* Date range */}
            <Box
              sx={{ width: localFilters.date_range.active ? "100%" : "48%" }}
            >
              <EGCheckbox
                checked={localFilters.date_range.active}
                onChange={handleFilterChange("date_range", "active")}
                label={t("Filter.date_range")}
                color="primary"
              />
              {localFilters.date_range.active && (
                <Stack direction="row" spacing={1}>
                  <EGDatePicker
                    label={`${t("Filter.from")}:`}
                    value={dayjs(localFilters.date_range.start)}
                    onChange={(d) =>
                      handleFilterChange("date_range", "start")(d)
                    }
                    maxDate={dayjs(localFilters.date_range.end)}
                  />

                  <EGDatePicker
                    label={`${t("Filter.to")}:`}
                    value={dayjs(localFilters.date_range.end)}
                    onChange={(d) => handleFilterChange("date_range", "end")(d)}
                    minDate={dayjs(localFilters.date_range.start)}
                    maxDate={dayjs()}
                  />
                </Stack>
              )}
            </Box>
            {/* Path */}
            <Box width={localFilters.path.active ? "100%" : "48%"}>
              <EGCheckbox
                checked={localFilters.path.active}
                onChange={handleFilterChange("path", "active")}
                label={t("Bitacora.event")}
                color="primary"
              />
              {localFilters.path.active && (
                <EGBasicSelect
                  options={eventOptions}
                  value={localFilters.path.value}
                  onChange={handleFilterChange("path")}
                  label={`${t("Bitacora.event")}:`}
                />
              )}
            </Box>
            {/* User email */}
            <Box width={localFilters.user_email.active ? "100%" : "48%"}>
              <EGCheckbox
                checked={localFilters.user_email.active}
                onChange={handleFilterChange("user_email", "active")}
                label={t("Bitacora.user")}
                color="primary"
              />
              {localFilters.user_email.active && (
                <EGInput
                  label={`${t("Bitacora.user")}:`}
                  value={localFilters.user_email.value}
                  onChange={handleFilterChange("user_email")}
                  type="email"
                />
              )}
            </Box>
            {/* Folio */}
            <Box width={localFilters.folio.active ? "100%" : "48%"}>
              <EGCheckbox
                label="Folio"
                checked={localFilters.folio.active}
                onChange={handleFilterChange("folio", "active")}
                color="primary"
              />
              {localFilters.folio.active && (
                <EGInput
                  label={t("Folio")}
                  value={localFilters.folio.value}
                  onChange={handleFilterChange("folio")}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            pb: 2,
            px: { xs: 0.5, md: 3 },
            display: "flex",
            justifyContent: {
              xs: "center",
              md: "space-between",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
            alignItems: {
              xs: "center",
              md: "stretch",
            },
            gap: 1,
          }}
        >
          {/* Reset filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-start" },
              minWidth: "160px", 
              mt: { xs: 1, md: 0 },
            }}
          >
            {hasLocalChanges ? (
              <EGButton
                label={t("Bitacora.reset_filters")}
                onClick={handleClearLocal}
                variant="outlined"
                iconName="filter_remove"
                color="error"
                iconColor="error"
                iconColorHover="white"
              />
            ) : null}
          </Box>

          {/* Apply - cancel */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: "flex-end" },
            }}
          >
            <EGButton
              label={t("Filter.apply_filter")}
              color="primary"
              onClick={handleSubmit}
            />
            <EGButton label={t("Filter.cancel")} onClick={handleClose} />
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
